'use client';

import { TEST_TEXT, SPECIFIC_CHART, LINE_HEIGHT, defaultFont, X_LABEL_HEIGHT, CHART_START_DRAWING } from './config.js';
import drawing from './drawing.js';

function getLargestTextWidth(ctx, textList) {
    if (!ctx)
        return 0;
    var textWidthList = textList.map(function (item) { return ctx.measureText(item).width; });
    return Math.max.apply(Math, textWidthList);
}
function responsiveHelper(_a) {
    var type = _a.type, ctx = _a.ctx, xRange = _a.xRange, chartStartingPointInYAxis = _a.chartStartingPointInYAxis, chartEndingPointInYAxis = _a.chartEndingPointInYAxis, chartStartingPointInXAxis = _a.chartStartingPointInXAxis, chartInformation = _a.chartInformation, axesConfig = _a.axesConfig;
    var xAxis = chartInformation.xAxis, yAxis = chartInformation.yAxis;
    /*
     * Coordinate of y-axis and x-axis label
     * */
    var startLabelingInYAxis = chartStartingPointInYAxis + X_LABEL_HEIGHT;
    var startLabelingInXAxis = chartStartingPointInXAxis;
    /*
     * Check single data
     * */
    if (SPECIFIC_CHART.includes(type)) {
        startLabelingInXAxis += xRange / 2;
    }
    /*
     * Calculate the increased text height if text is wrapped
     * */
    var _b = axesConfig || {}, decorationForHorizontalLabel = _b.decorationForHorizontalLabel, _c = _b.hideXAxis, hideXAxis = _c === void 0 ? false : _c;
    var largestTextInLabels = 0;
    var textHeight = 0;
    var convertedLabelOnXAxis = xAxis.map(function (item) {
        return decorationForHorizontalLabel ? decorationForHorizontalLabel(item) : item;
    });
    convertedLabelOnXAxis.forEach(function (item) {
        var _a = drawing.wrapText(ctx, item.toString(), startLabelingInXAxis, startLabelingInYAxis, xRange, LINE_HEIGHT), largestTextWidth = _a.largestTextWidth, textHeightHasIncreased = _a.textHeightHasIncreased;
        if (largestTextInLabels < largestTextWidth) {
            largestTextInLabels = largestTextWidth;
        }
        if (textHeight < textHeightHasIncreased) {
            textHeight = textHeightHasIncreased;
        }
        startLabelingInXAxis += xRange;
    });
    /*
     * Reset the increased text height if i hidden x-axis
     * The text height now is equal to the negative X_LABEL_HEIGHT
     * */
    if (hideXAxis) {
        textHeight = -X_LABEL_HEIGHT;
    }
    var yRange = (chartStartingPointInYAxis - (chartEndingPointInYAxis + textHeight)) / (yAxis.length - 1);
    var rsChartStartingPointInYAxis = chartStartingPointInYAxis - textHeight;
    var xLabelTextMaxLength = largestTextInLabels;
    return {
        yRange: yRange,
        chartStartingPointInYAxis: rsChartStartingPointInYAxis,
        xLabelTextMaxLength: xLabelTextMaxLength,
        startLabelingInXAxis: startLabelingInXAxis,
        startLabelingInYAxis: startLabelingInYAxis
    };
}
function getNecessaryInformation(ctx, chartInformation, width, height, config) {
    var _a;
    /* const { dataRange, dataLabels, labels } = chartInformation; */
    var yAxis = chartInformation.yAxis, xAxis = chartInformation.xAxis;
    var type = config.chart.type;
    var _b = ((_a = config.default) === null || _a === void 0 ? void 0 : _a.font) || {}, _c = _b.weight, weight = _c === void 0 ? defaultFont.weight : _c, _d = _b.size, size = _d === void 0 ? defaultFont.size : _d, _e = _b.family, family = _e === void 0 ? defaultFont.family : _e;
    /*
     * Set default font
     * */
    ctx.font = "".concat(weight, " ").concat(size, "px ").concat(family);
    /*
     * End set default font
     * */
    /* Get config */
    var decorationForVerticalLabel = ((config === null || config === void 0 ? void 0 : config.axes) || {}).decorationForVerticalLabel;
    var convertedDataRange = yAxis.map(function (item) {
        return decorationForVerticalLabel ? decorationForVerticalLabel(item) : item.toString();
    });
    var labelLengthInYAxis = getLargestTextWidth(ctx, convertedDataRange);
    var _f = ctx.measureText(TEST_TEXT), actualBoundingBoxAscent = _f.actualBoundingBoxAscent, actualBoundingBoxDescent = _f.actualBoundingBoxDescent;
    var textHeight = actualBoundingBoxAscent + actualBoundingBoxDescent;
    var chartStartingPointInYAxis = height - X_LABEL_HEIGHT - textHeight;
    var chartEndingPointInYAxis = textHeight / 2 + 1;
    var chartStartingPointInXAxis = labelLengthInYAxis + CHART_START_DRAWING;
    /*
     * Detect when last label in x-axis overflow over chart area
     *
     * */
    var largestTextWidth = drawing.wrapText(ctx, xAxis[xAxis.length - 1].toString(), 0, 0, 1, 10).largestTextWidth;
    var rangeInXAxis = (width / xAxis.length - 1) / 2;
    var lastLabelLengthInXAxis = 0;
    if (largestTextWidth / 2 - rangeInXAxis > 0) {
        /*
         * If text is overflow, i will calculate the width of overflow part by
         * minus half of largestTextWidth with range in xAxis         * */
        lastLabelLengthInXAxis = largestTextWidth / 2 - rangeInXAxis + 2;
    }
    if (SPECIFIC_CHART.includes(type)) {
        /*
         * For specific chart, just use the half of textWidth
         * */
        var width_1 = ctx.measureText(xAxis[xAxis.length - 1].toString()).width;
        lastLabelLengthInXAxis = width_1 / 2;
    }
    var chartEndingPointInXAxis = width - lastLabelLengthInXAxis;
    var yAxisLabelAt = 0; // Labeling position in y-axis
    /*
     * Check if data is single or not
     * */
    var labelsLength = xAxis.length;
    if (SPECIFIC_CHART.includes(type)) {
        labelsLength = xAxis.length - 1;
    }
    var xRange = (chartEndingPointInXAxis - chartStartingPointInXAxis) / labelsLength; // Range between XAxis threshold
    var _g = responsiveHelper({
        type: type,
        ctx: ctx,
        xRange: xRange,
        chartStartingPointInYAxis: chartStartingPointInYAxis,
        chartEndingPointInYAxis: chartEndingPointInYAxis,
        chartStartingPointInXAxis: chartStartingPointInXAxis,
        chartInformation: chartInformation,
        axesConfig: config === null || config === void 0 ? void 0 : config.axes
    }), rsChartStartingPointInYAxis = _g.chartStartingPointInYAxis, rsYRange = _g.yRange, xLabelTextMaxLength = _g.xLabelTextMaxLength, startLabelingInXAxis = _g.startLabelingInXAxis, startLabelingInYAxis = _g.startLabelingInYAxis;
    return {
        chartArea: {
            chartStartingPointInXAxis: chartStartingPointInXAxis,
            chartEndingPointInXAxis: chartEndingPointInXAxis,
            chartStartingPointInYAxis: rsChartStartingPointInYAxis,
            chartEndingPointInYAxis: chartEndingPointInYAxis
        },
        yAxisLabelAt: yAxisLabelAt,
        xRange: xRange,
        yRange: rsYRange,
        yLabelTextMaxLength: labelLengthInYAxis,
        xLabelTextMaxLength: xLabelTextMaxLength,
        startLabelingInXAxis: startLabelingInXAxis,
        startLabelingInYAxis: startLabelingInYAxis
    };
}
/*
 * Get calculate data point position in y-axis
 * @param {ChartInformation} chartInformation
 * @param {number} chartStartingPointInYAxis
 * @param {number} chartEndingPointInYAxis
 * @data {number} data
 * */
function getPointCoordinateInYAxis(props, controlRatio) {
    var chartInformation = props.chartInformation, data = props.data, chartStartingPointInYAxis = props.chartStartingPointInYAxis, chartEndingPointInYAxis = props.chartEndingPointInYAxis;
    var yAxis = chartInformation.yAxis;
    var maxValue = yAxis[0] || 0;
    var ratio = (data / maxValue) * controlRatio;
    var controlArea = (chartStartingPointInYAxis - chartEndingPointInYAxis) * ratio;
    var pointCoordinateInYAxis = chartStartingPointInYAxis - controlArea;
    return { pointCoordinateInYAxis: pointCoordinateInYAxis, ratio: ratio };
}
var calculation = {
    getLargestTextWidth: getLargestTextWidth,
    getNecessaryInformation: getNecessaryInformation,
    getPointCoordinateInYAxis: getPointCoordinateInYAxis
};

export { calculation as default };
//# sourceMappingURL=calculation.js.map
